<template>
  <div class="Search">
    <form
        @submit="doSearch($event)"
        class="Search-form">
      <toggle
          class="Search-toggle"
          @change="searchType($event)"
          :selected="searchBy"
          :options="['ticket', 'event', 'user']">
      </toggle>

      <toggle
          class="Search-toggle"
          @change="upcomingToggle($event === 'upcoming')"
          :selected="onlyUpcoming ? 'upcoming' : 'all'"
          :options="['all', 'upcoming']">
      </toggle>

      <input-field
          class="Search-field"
          v-model="search"
          @change="search = $event"
          placeholder="Search"
          :definition="{
          label: 'Search '+ searchBy+'s',
        }">
      </input-field>
      <button
          class="Button Search-button"
          type="submit">
        Search
      </button>
      <div
          v-if="asyncFormError">
        <p>{{ asyncFormError }}</p>
      </div>
    </form>


    <div class="FlexTable" v-if="previousSearch !== ''">
      <div class="FlexTable-header">
        <h3 class="FlexTable-title">
          {{ searchResults.data.length }} search result<span v-if="searchResults.data.length > 1">s</span> for <span
            class="Chip">{{ previousSearch }}</span>
          <span class="Chip" v-if="onlyUpcoming">Upcoming only</span>
        </h3>
        <h3 class="FlexTable-title" v-if="searchResults.data.length > 0">
          Searched by <span class="Chip">{{ searchResults.searchType }}</span>
        </h3>
      </div>
      <p
          class="FlexTable-content FlexTable-contentItem"
          v-if="searchResults.data.length === 0">
        No results
      </p>
      <ve-table
          class="Payments-table"
          row-key-field-name="id"
          :fixed-header="true"
          :columns="tableColumns"
          :table-data="searchResultsData"
          :cell-style-option="cellStyleOption"
          :editOption="editOption"
          :rowStyleOption="rowStyleOption"
          :expand-option="expandOption"
          :clipboard-option="{copy:true}"
      />
    </div>
    <transfer
        v-if="showTransfer"
        :ticket="transferSubject"
        @close="showTransfer = !$event; transferSubject = false"
        @activity-recorded="onTicketActivityRecorded">
    </transfer>
    <refund
        v-if="showRefund"
        :ticket="refundSubject"
        @close="showRefund = !$event; refundSubject = false"
        @activity-recorded="onTicketActivityRecorded">
    </refund>
    <ticket-history
        v-if="showTicketHistory && historySubject"
        ref="ticketHistory"
        :ticket-code="historySubject.code"
        :purchased-at="historySubject.timestamp"
        @close="closeTicketHistory">
    </ticket-history>

    <!-- GDPR Remove User Data Confirmation Dialog -->
    <div v-if="removeDataConfirm" class="Search-confirmationOverlay" @click.self="cancelRemoveUserData">
    <div class="Search-confirmation Card Card--top">
      <div class="Card-header">
        <h3 class="Card-title">Remove User Data (GDPR)</h3>
      </div>
      <div class="Card-body">
        <p>Are you sure you want to remove this user's data? This action:</p>
        <ul>
          <li>Anonymises the user record and ticket purchaser data</li>
          <li>Deletes the auth account (user cannot log in)</li>
          <li>Preserves events, ticket codes and Stripe audit trail for tax compliance</li>
        </ul>
        <p v-if="removeDataConfirm.type === 'user'">
          <strong>User:</strong> {{ removeDataConfirm.row.forename }} {{ removeDataConfirm.row.surname }}
          (ID: {{ removeDataConfirm.row.id }}, {{ removeDataConfirm.row.email }})
        </p>
        <p v-else>
          <strong>Ticket purchaser:</strong> {{ removeDataConfirm.row.email }} (Ticket: {{ removeDataConfirm.row.code }})
        </p>
        <p class="Search-confirmationWarning">This action cannot be undone.</p>
        <div class="Search-confirmationActions">
          <button
            class="Button Button--danger"
            :disabled="removeDataLoading"
            @click="confirmRemoveUserData">
            {{ removeDataLoading ? 'Removing…' : 'Remove data' }}
          </button>
          <button
            class="Button Button--secondary"
            :disabled="removeDataLoading"
            @click="cancelRemoveUserData">
            Cancel
          </button>
        </div>
      </div>
    </div>
    </div>

    <!-- Floating kebab menu (position: fixed to avoid table overflow clipping) -->
    <div
      v-if="kebabMenuOpen"
      class="Search-kebabMenu Search-kebabMenu--fixed"
      :style="kebabMenuPosition ? { top: kebabMenuPosition.top + 'px', left: kebabMenuPosition.left + 'px' } : {}"
      @click.stop>
      <button
        v-if="kebabMenuContext && kebabMenuContext.type === 'ticket'"
        class="Search-kebabMenuItem"
        @click="openHistoryFromKebab">
        History
      </button>
      <button
        v-if="kebabMenuContext"
        class="Search-kebabMenuItem Search-kebabMenuItem--danger"
        @click="requestRemoveUserData(kebabMenuContext.type, kebabMenuContext.row)">
        Remove user data (GDPR)
      </button>
    </div>

    <!-- Event details pane (ticket search only) -->
    <div
      v-if="eventInfoOpen && eventInfoContext"
      class="Search-eventInfoPane Search-eventInfoPane--fixed"
      :style="eventInfoPosition ? { top: eventInfoPosition.top + 'px', left: eventInfoPosition.left + 'px' } : {}"
      @click.stop>
      <p class="Search-eventInfoTitle">{{ eventInfoContext.event.name }}</p>
      <dl class="Search-eventInfoDetails">
        <div
          v-for="line in eventInfoLines"
          :key="line.label"
          class="Search-eventInfoRow">
          <dt>{{ line.label }}</dt>
          <dd>
            <a
              v-if="line.href"
              :href="line.href"
              target="_blank"
              rel="noopener noreferrer">{{ line.value }}</a>
            <span v-else>{{ line.value }}</span>
          </dd>
        </div>
      </dl>
    </div>

    <form-wrapper
      v-if="showForm"
      @close="showForm = !$event"
      :title="formTitle"
      :schema="formSchema"
      :url="formUrl"></form-wrapper>
  </div>
</template>

<script>
import './Search.scss';

import axios from "axios";
import errorModel from '../../models/error';
import cookieModel from '../../models/cookie';
import toastModel from '../../models/toast';
import Transfer from "../../components/transfer/transfer";
import Refund from "../../components/refund.vue";
import TicketHistory from "../../components/ticket-history.vue";
import InputField from "../../components/input-field/input-field.vue";
import Toggle from "../../components/toggle.vue";
import $router from '../../router';
import FormWrapper from "../../components/form-wrapper/form-wrapper.vue";
import {toArray} from "lodash";
import {
  buildEventEditUrl,
  buildTicketResendPayload,
  buildUserDashboardUrl,
  buildUserSearchRoute,
  getOrganiserInfo,
  getSearchStateFromQuery,
  getTicketEventDetailLines,
  isResendTicketDisabled,
  isTicketResendQueued,
  TICKET_RESEND_PATH,
} from "./search-utils";

export default {
  components: {
    FormWrapper,
    Transfer,
    Refund,
    TicketHistory,
    InputField,
    Toggle,
  },
  mounted() {
      const urlChange = () => {
      const queryParams = $router.currentRoute.query;
      this.syncFromRoute(queryParams);

      if (queryParams) {
        this.doSearch();
      }
    };

    window.addEventListener('popstate', urlChange);
    document.addEventListener('click', this.closeFloatingMenus);

    // Useful for cleanup when component is destroyed
    this.cleanup = urlChange;

    urlChange();
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.cleanup);
    document.removeEventListener('click', this.closeFloatingMenus);
  },
  props: {},
  computed: {
    eventInfoLines: function () {
      if (!this.eventInfoContext || !this.eventInfoContext.event) {
        return [];
      }
      return getTicketEventDetailLines(this.eventInfoContext.event);
    },
  },
  watch: {
    '$route.query': {
      handler(queryParams) {
        this.syncFromRoute(queryParams);
        if (queryParams && (queryParams.search || queryParams.searchType || queryParams.onlyUpcoming)) {
          this.doSearch();
        }
      },
      deep: true,
    },
  },
  methods: {
    // do search
    doSearch: function (evt) {
      if (evt) {
        evt.preventDefault();
      }
      const vm = this;
      let url = '';

      switch (vm.searchBy) {
        case 'ticket':
        default:
          url = this.baseUrl + "/einstein/ticket/search";
          vm.tableColumns = vm.ticketColumns;
          break;
        case 'event':
          url = this.baseUrl + "/events/search";
          vm.tableColumns = vm.eventColumns;
          break;
        case 'user':
          url = this.baseUrl + "/einstein/user/search";
          vm.tableColumns = vm.userColumns;
          break;
      }

      // reset error message
      vm.asyncFormError = '';
      let postData = {};

      if (vm.search.length < 2) {
        vm.searchResults = [];
        vm.previousSearch = '';
        vm.asyncFormError = "Please enter at least 2 characters";
        return;
      }
      postData['search'] = vm.search;
      postData['upcomingOnly'] = vm.onlyUpcoming;

      vm.updateQueryParams({
        'search': vm.search,
        'searchType': vm.searchBy,
        'onlyUpcoming': vm.onlyUpcoming,
      });

      // Add authorisation to request header
      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      vm.axiosInstance.post(url, postData)
          .then(function (response) {
            if (response.data && response.data.status === "success") {
              // reset error
              vm.asyncFormError = false;
              vm.previousSearch = vm.search;
              vm.searchResults = response.data;
              vm.searchResultsData = toArray(response.data.data);
              vm.disableForm = false;
            } else {
              vm.asyncFormError = response.data.status + ': ' + response.data.message;
            }
          }).catch(function (error) {
        vm.asyncFormError = errorModel.apiError(error, vm.$router);
        vm.disableForm = false;
      });
    },
    transfer: function (ticket) {
      this.showTransfer = true;
      this.transferSubject = ticket;
    },
    refund: function (ticket) {
      this.showRefund = true;
      this.refundSubject = ticket;
    },
    showHistory: function (ticket) {
      this.historySubject = ticket;
      this.showTicketHistory = true;
    },
    closeTicketHistory: function () {
      this.showTicketHistory = false;
      this.historySubject = false;
    },
    onTicketActivityRecorded: function () {
      if (this.showTicketHistory && this.$refs.ticketHistory) {
        this.$refs.ticketHistory.reload();
      }
    },
    isResendDisabled: function (ticketCode) {
      return isResendTicketDisabled(ticketCode, {
        resentTicketCodes: this.resentTicketCodes,
        resendingTicketCode: this.resendingTicketCode,
      });
    },
    resendTicket: function (ticket) {
      const vm = this;
      const ticketCode = String(ticket.code);

      if (vm.isResendDisabled(ticketCode)) {
        return;
      }

      vm.resendingTicketCode = ticketCode;
      vm.asyncFormError = '';
      vm.axiosInstance.defaults.headers.common['Authorization'] =
        'Bearer ' + cookieModel.getCookie('ticketlab_access');

      vm.axiosInstance
        .post(vm.baseUrl + TICKET_RESEND_PATH, buildTicketResendPayload(ticketCode))
        .then(function (response) {
          vm.resendingTicketCode = null;
          if (isTicketResendQueued(response.data)) {
            if (!vm.resentTicketCodes.includes(ticketCode)) {
              vm.resentTicketCodes.push(ticketCode);
            }
            toastModel.show('Ticket resent');
            return;
          }
          if (response.data && response.data.status === 'error') {
            vm.asyncFormError = response.data.reason || 'Failed to resend ticket';
          }
        })
        .catch(function (error) {
          vm.resendingTicketCode = null;
          const responseError =
            error.response && error.response.data && error.response.data.error;
          vm.asyncFormError =
            errorModel.apiError(error, vm.$router) ||
            responseError ||
            'Failed to resend ticket';
        });
    },
    markEventAsSpam: function (event) {
      const vm = this;
      vm.asyncFormError = '';
      vm.disableForm = true;

      // Add authorisation to request header
      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      const postData = {
        event_id: event.id
      };

      vm.axiosInstance.post(vm.baseUrl + "/admin/spam/mark", postData)
        .then(function (response) {
          if (response.data && response.data.success) {
            // Update the event status in the local data
            event.status = 7;
            event.status_type = 'SPAM';
            vm.asyncFormError = false;
            vm.disableForm = false;
          } else {
            vm.asyncFormError = response.data.message || 'Failed to mark event as spam';
            vm.disableForm = false;
          }
        }).catch(function (error) {
          vm.asyncFormError = errorModel.apiError(error, vm.$router);
          vm.disableForm = false;
        });
    },
    showTickets: function (eventId) {
      const vm = this;
      vm.searchBy = 'ticket';
      vm.search = eventId;
      vm.upcomingOnly = false;
      vm.doSearch()
    },
    searchType: function (newType) {
      const vm = this;
      vm.searchResults = [];
      vm.previousSearch = '';
      vm.searchBy = newType
    },
    upcomingToggle: function (updatedValue) {
      this.onlyUpcoming = Boolean(updatedValue);
    },
    syncFromRoute: function (queryParams) {
      const state = getSearchStateFromQuery(queryParams);
      this.search = state.search;
      this.searchType(state.searchBy);
      this.upcomingToggle(state.onlyUpcoming);
    },
    optInDataRelease: function (row) {
      const vm = this;
      vm.asyncFormError = '';
      vm.disableForm = true;
      // Add authorisation to request header
      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      let postData = {
        'userId': row.id
      };

      // Do a call to the search api for guest name
      vm.axiosInstance.post(vm.baseUrl + "/einstein/merchant/data-release", postData)
          .then(function (response) {
            if (response.data && response.data.status === "success") {
              // reset error
              vm.asyncFormError = false;
              vm.disableForm = false;
              row.merchant = {};
              row.merchant.data_release = 1;
            } else {
              vm.asyncFormError = response.data.status + ': ' + response.data.message;
              vm.disableForm = false;
            }
          }).catch(function (error) {
            vm.asyncFormError = errorModel.apiError(error, vm.$router);
            vm.disableForm = false;
          }
      );
    },
    addBankDetailsForm: function (row) {
      // Add bank details form
      const vm = this;
      vm.asyncFormError = '';
      vm.disableForm = true;

      vm.formTitle = "Add bank details for " + row.forename + " " + row.surname;
      vm.formUrl = vm.baseUrl + "/settings/add-bank-account-details";
      vm.formSchema = [
        {
          "type": "hidden",
          "name": "user_id",
          "model": row.id.toString(),
          "class": "Input--hidden"
        },
        {
          "type": "text",
          "name": "account_name",
          "label": "Account name",
          "placeholder": "Account name",
          "model": "",
          "required": true,
          "class": "Input--accountName"
        },
        {
          "type": "number",
          "name": "sort_code",
          "label": "Sort code",
          "placeholder": "XXXXXX",
          "model": "",
          "required": true,
          "class": "Input--sortCode"
        },
        {
          "type": "number",
          "name": "account_number",
          "label": "Account number",
          "placeholder": "XXXXXXXX",
          "model": "",
          "required": true,
          "class": "Input--accountNumber"
        }
      ];
      vm.showForm = true;
    },
    updateQueryParams: function (params) {
      history.pushState(
        {},
        null,
        '/#' + this.$router.currentRoute.path +
        '?' +
        Object.keys(params)
          .map(key => {
            return (
                encodeURIComponent(key) + '=' + encodeURIComponent(params[key])
            )
          })
          .join('&')
      )
    },
    blockUser: function (user) {
      const vm = this;
      vm.asyncFormError = '';
      vm.disableForm = true;

      // Add authorisation to request header
      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      vm.axiosInstance.post(vm.baseUrl + "/admin/users/" + user.id + "/block")
        .then(function (response) {
          if (response.data && response.data.success) {
            // Update the user status in the local data
            user.status = 'Blocked';
            vm.asyncFormError = false;
            vm.disableForm = false;
            // Show success message
            vm.$nextTick(() => {
              vm.asyncFormError = response.data.message;
              setTimeout(() => {
                vm.asyncFormError = false;
              }, 3000);
            });
          } else {
            vm.asyncFormError = response.data.message || 'Failed to block user';
            vm.disableForm = false;
          }
        }).catch(function (error) {
          vm.asyncFormError = errorModel.apiError(error, vm.$router);
          vm.disableForm = false;
        });
    },
    blockUserAndMarkSpam: function (user) {
      const vm = this;
      vm.asyncFormError = '';
      vm.disableForm = true;

      // Add authorisation to request header
      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      vm.axiosInstance.post(vm.baseUrl + "/admin/users/" + user.id + "/block-and-mark-spam")
        .then(function (response) {
          if (response.data && response.data.success) {
            // Update the user status in the local data
            user.status = 'Blocked';
            vm.asyncFormError = false;
            vm.disableForm = false;
            // Show success message
            vm.$nextTick(() => {
              vm.asyncFormError = response.data.message;
              setTimeout(() => {
                vm.asyncFormError = false;
              }, 5000);
            });
          } else {
            vm.asyncFormError = response.data.message || 'Failed to block user and mark events as spam';
            vm.disableForm = false;
          }
        }).catch(function (error) {
          vm.asyncFormError = errorModel.apiError(error, vm.$router);
          vm.disableForm = false;
        });
    },
    trustUser: function (user) {
      const vm = this;
      vm.asyncFormError = '';
      vm.disableForm = true;

      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      vm.axiosInstance.post(vm.baseUrl + "/admin/users/" + user.id + "/trust")
        .then(function (response) {
          if (response.data && response.data.success) {
            user.user_type = 2;
            vm.asyncFormError = false;
            vm.disableForm = false;
            // Show success message
            vm.$nextTick(() => {
              vm.asyncFormError = response.data.message;
              setTimeout(() => {
                vm.asyncFormError = false;
              }, 3000);
            });
          } else {
            vm.asyncFormError = response.data.message || 'Failed to trust user';
            vm.disableForm = false;
          }
        }).catch(function (error) {
          vm.asyncFormError = errorModel.apiError(error, vm.$router);
          vm.disableForm = false;
        });
    },
    untrustUser: function (user) {
      const vm = this;
      vm.asyncFormError = '';
      vm.disableForm = true;

      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      vm.axiosInstance.post(vm.baseUrl + "/admin/users/" + user.id + "/untrust")
        .then(function (response) {
          if (response.data && response.data.success) {
            user.user_type = 1;
            vm.asyncFormError = false;
            vm.disableForm = false;
            // Show success message
            vm.$nextTick(() => {
              vm.asyncFormError = response.data.message;
              setTimeout(() => {
                vm.asyncFormError = false;
              }, 3000);
            });
          } else {
            vm.asyncFormError = response.data.message || 'Failed to remove user trust';
            vm.disableForm = false;
          }
        }).catch(function (error) {
          vm.asyncFormError = errorModel.apiError(error, vm.$router);
          vm.disableForm = false;
        });
    },
    requestRemoveUserData: function (type, row) {
      this.closeFloatingMenus();
      this.removeDataConfirm = { type, row };
    },
    cancelRemoveUserData: function () {
      this.removeDataConfirm = null;
      this.removeDataLoading = false;
    },
    confirmRemoveUserData: function () {
      const vm = this;
      const { type, row } = vm.removeDataConfirm;
      vm.removeDataLoading = true;

      vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

      const body = type === 'user'
        ? { userId: row.id }
        : { email: row.email };

      vm.axiosInstance.post(vm.baseUrl + '/einstein/user/remove-data', body)
        .then(function (response) {
          vm.removeDataLoading = false;
          vm.removeDataConfirm = null;
          if (response.data && response.data.status === 'success') {
            vm.asyncFormError = false;
            vm.$nextTick(() => {
              vm.asyncFormError = 'User data removed successfully.';
              setTimeout(() => { vm.asyncFormError = false; }, 5000);
            });
            vm.removeRowAfterDataRemoval(type, row);
          } else {
            vm.asyncFormError = (response.data && response.data.data) || 'Failed to remove user data';
          }
        })
        .catch(function (error) {
          vm.removeDataLoading = false;
          vm.asyncFormError = errorModel.apiError(error, vm.$router) || 'Failed to remove user data';
        });
    },
    removeRowAfterDataRemoval: function (type, row) {
      const idx = this.searchResultsData.findIndex((r) => {
        if (type === 'user') return r.id === row.id;
        return r.code === row.code;
      });
      if (idx !== -1) {
        this.searchResultsData.splice(idx, 1);
        if (this.searchResults && this.searchResults.data) {
          const dataIdx = this.searchResults.data.findIndex((r) => {
            if (type === 'user') return r.id === row.id;
            return r.code === row.code;
          });
          if (dataIdx !== -1) {
            this.searchResults.data.splice(dataIdx, 1);
          }
        }
      }
    },
    toggleKebabMenu: function (key, row, type, event) {
      if (this.kebabMenuOpen === key) {
        this.closeKebabMenu();
        return;
      }
      this.closeEventInfo();
      if (event && event.target) {
        const rect = event.target.getBoundingClientRect();
        this.kebabMenuPosition = {
          top: rect.bottom + 2,
          left: rect.left
        };
      } else {
        this.kebabMenuPosition = { top: 0, left: 0 };
      }
      this.kebabMenuContext = { type, row };
      this.kebabMenuOpen = key;
    },
    closeKebabMenu: function () {
      this.kebabMenuOpen = null;
      this.kebabMenuPosition = null;
      this.kebabMenuContext = null;
    },
    openHistoryFromKebab: function () {
      const row = this.kebabMenuContext && this.kebabMenuContext.row;
      this.closeFloatingMenus();
      if (row) {
        this.showHistory(row);
      }
    },
    toggleEventInfo: function (key, eventData, domEvent) {
      if (this.eventInfoOpen === key) {
        this.closeEventInfo();
        return;
      }
      this.closeKebabMenu();
      if (domEvent && domEvent.target) {
        const rect = domEvent.target.getBoundingClientRect();
        this.eventInfoPosition = {
          top: rect.bottom + 4,
          left: Math.max(8, rect.left - 140),
        };
      } else {
        this.eventInfoPosition = { top: 0, left: 0 };
      }
      this.eventInfoContext = { event: eventData };
      this.eventInfoOpen = key;
    },
    closeEventInfo: function () {
      this.eventInfoOpen = null;
      this.eventInfoPosition = null;
      this.eventInfoContext = null;
    },
    closeFloatingMenus: function () {
      this.closeKebabMenu();
      this.closeEventInfo();
    }
  },
  data: function () {
    return {
      baseUrl: process.env.VUE_APP_API_URL,
      venueId: process.env.VUE_APP_VENUE_ID,
      axiosInstance: axios.create({
        baseURL: this.baseUrl,
      }),
      asyncFormError: false,
      disableForm: false,
      userToken: '',
      search: '',
      searchResults: [],
      searchResultsData: [],
      previousSearch: '',
      tableColumns: [],
      ticketColumns: [
        {
          field: "type",
          key: "type",
          title: "Type",
          align: "left",
          operationColumn: true,
          type: "expand",
        },
        {
          field: "code",
          key: "code",
          title: "Code",
          align: "left"
        },
        {
          field: "name",
          key: "name",
          title: "Name",
          align: "left",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            return (
                <span>
                {row.name}
              </span>
            );
          },
        },
        {
          field: "email",
          key: "email",
          title: "Email",
          align: "left",
          edit: true,
        },
        {
          field: "event.name",
          key: "eventName",
          title: "Event",
          align: "left",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            const infoKey = 'event-' + row.code;
            return (
                <span class="Search-eventCell">
                <a href={row.event.url}>{row.event.name}</a>
                <button
                  type="button"
                  class="Search-eventInfoButton"
                  title="Event details"
                  aria-label="Show event details"
                  aria-expanded={this.eventInfoOpen === infoKey ? 'true' : 'false'}
                  on-click={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    this.toggleEventInfo(infoKey, row.event, e);
                  }}>
                  ⓘ
                </button>
              </span>
            );
          },
        },
        {
          field: "price",
          key: "c",
          title: "Price",
          align: "right",
          type: "currency",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            return (
                <span>
                {row.event.currency_symbol}{row.event.price}
              </span>
            );
          },
        },
        {
          field: "quantity",
          key: "quantity",
          title: "Quantity sold",
          align: "right"
        },
        {
          field: "has_discount_codes",
          key: "discountCodes",
          title: "Has discount codes?",
          align: "center",
          width: "200px",
          type: "boolean",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row, column, rowIndex}, h) => {
            return (
                <span>
                <md-checkbox v-model={row.has_discount_codes} disabled></md-checkbox>
              </span>
            );
          },
        },
        {
          field: "",
          key: "actions",
          title: "Action",
          width: "",
          center: "left",
          renderBodyCell: ({row, column, rowIndex}, h) => {
            const ticketKebabKey = 'ticket-' + row.code;
            return (
                <span>
                <button
                    class="Button"
                    on-click={() => this.resendTicket(row)}
                    disabled={this.isResendDisabled(row.code)}>
                  {this.resendingTicketCode === String(row.code) ? 'Resending…' : 'Resend ticket'}
                </button>
                <button
                    class="Button"
                    on-click={() => this.transfer(row)}>
                  Transfer
                </button>
                <button
                    class="Button"
                    on-click={() => this.refund(row)}
                    disabled={row.status === 8}>
                  {row.event.price > 0 ? 'Refund ticket' : 'Cancel ticket'}
                </button>
                <span class="Search-kebabWrap">
                  <button
                    class="Search-kebabButton"
                    title="More actions"
                    on-click={(e) => { e.stopPropagation(); e.preventDefault(); this.toggleKebabMenu(ticketKebabKey, row, 'ticket', e); }}>
                    ⋮
                  </button>
                </span>
              </span>
            );
          },
        },
      ],
      eventColumns: [
        {
          field: "id",
          key: "id",
          title: "ID",
          align: "right"
        },
        {
          field: "status_type",
          key: "status_type",
          title: "Status",
          align: "left",
        },
        {
          field: "name",
          key: "name",
          title: "Name",
          align: "left"
        },
        {
          field: "event.date",
          key: "eventDate",
          title: "Event Date",
          align: "left",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            const date = row.date;
            return (
                <span>
                {date}
              </span>
            );
          },
        },
        {
          field: "price",
          key: "c",
          title: "Price",
          align: "right",
          type: "currency",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            return (
                <span>
                {row.currency_symbol}{row.price}
              </span>
            );
          },
        },
        {
          field: "quantity",
          key: "quantity",
          title: "Quantity",
          align: "right"
        },
        {
          field: "sold",
          key: "sold",
          title: "Sold",
          align: "right"
        },
        {
          field: "user_id",
          key: "organiser",
          title: "Organiser",
          align: "left",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            const organiser = getOrganiserInfo(row);
            const organiserId = organiser.id;
            const organiserName = organiser.name;
            const searchRoute = organiserId ? buildUserSearchRoute(organiserId) : null;
            return (
              <span>
                id: {searchRoute ? <RouterLink to={searchRoute}>{organiserId}</RouterLink> : organiserId}
                <br/>
                {organiserName}
              </span>
            );
          },
        },
        {
          field: "locale",
          key: "locale",
          title: "Locale",
          align: "left"
        },
        {
          field: "payment_date",
          key: "paymentDate",
          title: "Payment Date",
          align: "left"
        },
        {
          field: "has_discount_codes",
          key: "discountCodes",
          title: "Has discount codes?",
          align: "center",
          width: "200px",
          type: "boolean",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row, column, rowIndex}, h) => {
            return (
                <span>
                <md-checkbox v-model={row.has_discount_codes} disabled></md-checkbox>
              </span>
            );
          },
        },
        {
          field: "",
          key: "actions",
          title: "Action",
          width: "",
          center: "left",
          renderBodyCell: ({row, column, rowIndex}, h) => {
            return (
                <span>
                <a
                    class="Button"
                    href={buildEventEditUrl(row.id)}
                    target="_blank"
                    rel="noopener noreferrer">
                  Edit
                </a>
                <button
                    class="Button"
                    on-click={() => this.showTickets(row.id)}>
                  Show Tickets
                </button>
                <button
                    class="Button"
                    on-click={() => this.markEventAsSpam(row)}
                    disabled={row.status === 7 || row.status_type === 'SPAM'}>
                  {row.status === 7 || row.status_type === 'SPAM' ? 'Marked as Spam' : 'Mark as Spam'}
                </button>
              </span>
            );
          },
        },
      ],
      userColumns: [
        {
          field: "id",
          key: "id",
          title: "ID",
          align: "right",
          operationColumn: true,
          type: "expand",
        },
        {
          field: "name",
          key: "name",
          title: "Name",
          align: "left",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            return (
                <span>
                {row.forename} {row.surname}
              </span>
            );
          },
        },
        {
          field: "email",
          key: "email",
          title: "Email",
          align: "left",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            const email = row.email ? row.email : '';
            const mailToEmail = row.email ? 'mailto:' + row.email : '';
            return (
                <span>
                <a href={mailToEmail}>{email}</a>
              </span>
            );
          },
        },
        {
          field: "tickets.length",
          key: "tickets.length",
          title: "Number of tickets",
          align: "right",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            return (
                <span>
                {row.tickets.length}
              </span>
            );
          },
        },
        {
          field: "events.length",
          key: "events.length",
          title: "Number of events",
          align: "right",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            return (
                <span>
                {row.events.length}
              </span>
            );
          },
        },
        {
          field: "timestamp",
          key: "timestamp",
          title: "Signed up",
          align: "left",
        },
        {
          field: "status",
          key: "status",
          title: "Status",
          align: "left",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            const status = row.status || 'Valid';
            const statusClass = status === 'Blocked' ? 'User-status--blocked' : 'User-status--valid';
            return (
              <span class={statusClass}>
                {status}
              </span>
            );
          },
        },
        {
          field: "user_type",
          key: "userType",
          title: "User Type",
          align: "left",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row}, h) => {
            const userType = row.user_type || 1;
            const userTypeText = userType === 0 ? 'Admin' : 
                               userType === 2 ? 'Trusted' : 
                               userType === 3 ? 'Artist' : 
                               userType === 4 ? 'Attendee' : 
                               userType === 5 ? 'Mailing List' : 'Promoter';
            const userTypeClass = userType === 2 ? 'User-type--trusted' : 'User-type--normal';
            return (
              <span class={userTypeClass}>
                {userTypeText}
              </span>
            );
          },
        },
        {
          field: "quantity",
          key: "quantity",
          title: "Quantity sold",
          align: "right"
        },
        {
          field: "merchant.data_release",
          key: "dataRelease",
          title: "Data Release",
          align: "center",
          width: "200px",
          type: "boolean",
          /* eslint-disable no-unused-vars */ // h is required for renderBodyCell to work
          renderBodyCell: ({row, column, rowIndex}, h) => {
            const dataRelease = !!(row.merchant && row.merchant.data_release);
            return (
                <span>
                <md-checkbox v-model={dataRelease} disabled></md-checkbox>
              </span>
            );
          },
        },
        {
          field: "",
          key: "actions",
          title: "Action",
          width: "",
          center: "left",
          renderBodyCell: ({row, column, rowIndex}, h) => {
            let content = [];

            if (!row.merchant || (row.merchant && !row.merchant.data_release)) {
              content.push(
                <button
                  class="Button"
                  on-click={() => this.optInDataRelease(row)}>
                  Opt in data release
                </button>
              );
            }

            content.push(
              <button
                class="Button"
                on-click={() => this.addBankDetailsForm(row)}>
                Add bank details
              </button>
            );

            content.push(
              <RouterLink
                class="Button"
                to={`/payments/due/${row.id}`}>
                View payment details
              </RouterLink>
            );

            content.push(
              <a
                class="Button"
                href={buildUserDashboardUrl(row.id)}
                target="_blank"
                rel="noopener noreferrer">
                User dashboard
              </a>
            );

            // Add user trust actions
            if (row.user_type === 2) {
              content.push(
                <button
                  class="Button Button--success"
                  on-click={() => this.untrustUser(row)}
                  disabled={this.disableForm}>
                  Remove Trust
                </button>
              );
            } else {
              content.push(
                <button
                  class="Button Button--info"
                  on-click={() => this.trustUser(row)}
                  disabled={this.disableForm}>
                  Trust User
                </button>
              );
            }

            // Add user blocking actions
            if (row.status !== 'Blocked') {
              content.push(
                <button
                  class="Button Button--warning"
                  on-click={() => this.blockUser(row)}
                  disabled={this.disableForm}>
                  Block User
                </button>
              );

              content.push(
                <button
                  class="Button Button--danger"
                  on-click={() => this.blockUserAndMarkSpam(row)}
                  disabled={this.disableForm}>
                  Block & Mark Events as Spam
                </button>
              );
            } else {
              content.push(
                <span class="Button Button--disabled" style="opacity: 0.6; cursor: not-allowed;">
                  User Blocked
                </span>
              );
            }

            // Kebab menu for GDPR remove user data (menu rendered at root with position: fixed)
            const userKebabKey = 'user-' + row.id;
            content.push(
              <span class="Search-kebabWrap">
                <button
                  class="Search-kebabButton"
                  title="More actions"
                  on-click={(e) => { e.stopPropagation(); e.preventDefault(); this.toggleKebabMenu(userKebabKey, row, 'user', e); }}>
                  ⋮
                </button>
              </span>
            );

            return (
              <span>
              {content}
              </span>
            );
          },
        },
      ],
      cellStyleOption: {
        bodyCellClass: ({row, column}) => {
          if (column.type === "boolean") {
            if (row[column.field] === true) {
              return "isTrue";
            }
          }
        },
      },
      rowStyleOption: {
        clickHighlight: true,
        hoverHighlight: false,
      },
      expandOption: {
        render: ({row, column, rowIndex}, h) => {
          return <TicketTable row={row}/>;
        },
      },
      // edit option
      editOption: {
        beforeCellValueChange: ({row, column, changeValue}) => {

          if (column.field === "number" && !/^\d+$/.test(changeValue)) {
            alert("please enter a number");
            return false;
          }
        },
        afterCellValueChange: ({row, column, changeValue}) => {

          const vm = this;

          const postData = {
            [column.key]: changeValue,
            'ticketCode': row.code
          }

          const url = this.baseUrl + "/einstein/ticket/edit/" + column.key;
          // Add authorisation to request header
          vm.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');

          // post updated value to server /api/einstein/ticket/edit/email
          vm.axiosInstance.post(url, postData)
              .then(function (response) {
                if (!(response.data && response.data.status === "success")) {
                  console.error('form error', response);
                  vm.asyncFormError = response.data.status + ': ' + response.data.message;
                }
              }).catch(function (error) {
            vm.asyncFormError = errorModel.apiError(error, vm.$router);
            vm.disableForm = false;
          });
        },
      },
      searchBy: 'ticket',
      resentTicketCodes: [],
      resendingTicketCode: null,
      showTransfer: false,
      showRefund: false,
      showTicketHistory: false,
      transferSubject: false,
      refundSubject: false,
      historySubject: false,
      onlyUpcoming: true,
      kebabMenuOpen: null,
      kebabMenuPosition: null,
      kebabMenuContext: null,
      eventInfoOpen: null,
      eventInfoPosition: null,
      eventInfoContext: null,
      removeDataConfirm: null,
      removeDataLoading: false,
      formSchema: {},
      formUrl: '',
      showForm: false,
      formTitle: '',
    }
  }
}
</script>
